import { useState } from "react"
import { css } from "@emotion/css"
import { useI18n } from "providers/I18n"

import Button from "ui/buttons/Button"
import * as DG from "ui/dialogs"
import { useMutation, queryCache, useQuery } from "react-query"

import { ApiError, ErrorsMap } from "api/http/utils"
import { fetchAgencyAvailableUsers } from "api/users"
import { AgencyTeam } from "../../TeamMembers/types"
import AutoCompleteInput from "components/inputs/AutoComplete"
import { addAgencyTeamMember } from "api/team"
import { useAgencyId } from "hooks/useAgencyId"

export type Props = {
  teamId: string
  open?: boolean
  onClose?: () => void
}

function AddTeamMember(props: Props) {
  const { teamId, open, onClose } = props
  const { t } = useI18n()
  const { agencyId } = useAgencyId()

  const [member, setMember] = useState<Partial<AgencyTeam>>()
  const [errors, setErrors] = useState<ErrorsMap | undefined>()

  const ps = new URLSearchParams({ excludeTeamId: teamId }).toString()
  const { data: availableUsers } = useQuery(
    ["agency-available-users", ps],
    fetchAgencyAvailableUsers,
  )

  const handleChange = (member: Partial<AgencyTeam> | null) => {
    !!member && setMember(member)
    setErrors(undefined)
  }

  const [create] = useMutation(addAgencyTeamMember, {
    onSuccess: () => {
      queryCache.invalidateQueries(`agency-team-${agencyId}`)
      queryCache.invalidateQueries("agency-available-users")
      setMember(undefined)
      onClose && onClose()
    },
    onError: (err: ApiError) => {
      const error = err as ApiError
      const errorMap = error?.errorsMap ?? {}
      setErrors(errorMap)
    },
  })

  const handleSubmit = () => {
    if (!!member?.id) {
      const pld = {
        teamId,
        memberId: member.id,
      }
      create(pld).catch((e) => {})
    }
  }

  const handleClose = () => {
    setMember(undefined)
    setErrors(undefined)
    onClose && onClose()
  }

  const id = "create-new-member"
  return (
    <DG.Dialog
      open={open || false}
      onClose={handleClose}
      id={id}
      disableEscapeKeyDown
      keepMounted
    >
      <DG.DialogHeader>
        <DG.DialogTitle id={id}>
          {t("teams.member.create.title")}
        </DG.DialogTitle>
        <DG.DialogClose onClose={handleClose} />
      </DG.DialogHeader>

      <DG.DialogContent>
        <div className={styles.inputWrapper}>
          <AutoCompleteInput<Partial<AgencyTeam>>
            key={member?.id}
            label={t("teams.member.input.name")}
            size="medium"
            margin="normal"
            options={availableUsers || []}
            onChange={handleChange}
            getOptionSelected={(option, value) => {
              return option.id === value.id
            }}
            renderOption={(option) => (
              <span>
                {option.name} ({option.role})
              </span>
            )}
            getOptionLabel={(option) => `${option.name} (${option.role})`}
            value={member}
            fetchOnFilter={false}
          />
          <div className={styles.errorMessage}>
            {t(errors?.users as string)}
          </div>
        </div>
      </DG.DialogContent>
      <DG.DialogFooter>
        <DG.DialogActions>
          <Button variant="outlined" color="primary" onClick={handleClose}>
            {t("teams.member.button.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            form="new_member"
            onClick={handleSubmit}
          >
            {t("teams.member.button.add")}
          </Button>
        </DG.DialogActions>
      </DG.DialogFooter>
    </DG.Dialog>
  )
}

const styles = {
  inputWrapper: css`
    margin-right: auto;
    margin-top: var(--spacing-s);
  `,
  input: css`
    min-width: 255px;
  `,
  errorMessage: css`
    font-size: 13px;
    color: var(--light-red);
  `,
}

export default AddTeamMember
